import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { GlassCard, GlassButton, GlassSelect } from '../../components/ui/GlassComponents';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement, Filler } from 'chart.js';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Analytics = () => {
  const { user, team } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [sickLeaveTypeId, setSickLeaveTypeId] = useState('');
  const [dateRange, setDateRange] = useState('current-year');
  const [departmentId, setDepartmentId] = useState('');
  const [departments, setDepartments] = useState([]);

  // Analytics data state
  const [overview, setOverview] = useState({});
  const [userBalances, setUserBalances] = useState([]);
  const [popularDays, setPopularDays] = useState({});
  const [topUsers, setTopUsers] = useState([]);
  const [sickLeave, setSickLeave] = useState({});
  const [loginActivity, setLoginActivity] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [trends, setTrends] = useState({});
  const [utilization, setUtilization] = useState({});
  const [approvalTimes, setApprovalTimes] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [isDarkMode, setIsDarkMode] = useState(document.documentElement.classList.contains('dark'));
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [showExpiringModal, setShowExpiringModal] = useState(false);
  const [payrollDialogOpen, setPayrollDialogOpen] = useState(false);
  const [payrollFrom, setPayrollFrom] = useState('');
  const [payrollTo, setPayrollTo] = useState('');
  const [payrollDownloading, setPayrollDownloading] = useState(false);

  useEffect(() => {
    fetchAnalyticsData();
    fetchTeamMembers();
    fetchLeaveTypes();
  }, [dateRange, departmentId]);

  useEffect(() => {
    // Load departments once for the filter dropdown. Failure is non-fatal —
    // the filter just won't appear (matches OurPeople's approach).
    axios.get('/api/departments')
      .then(r => setDepartments(r.data.departments || []))
      .catch(() => setDepartments([]));
    // Expiring-docs tile (admin-only endpoint; silent fail for non-admins).
    axios.get('/api/hr/documents/expiring?days=30')
      .then(r => setExpiringDocs(r.data.documents || []))
      .catch(() => setExpiringDocs([]));

    // Default payroll range = this month.
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const fmt = (d) => d.toISOString().slice(0, 10);
    setPayrollFrom(fmt(firstOfMonth));
    setPayrollTo(fmt(lastOfMonth));
  }, []);

  const downloadPayrollCsv = async () => {
    if (!payrollFrom || !payrollTo) {
      toast.error('Please pick both dates');
      return;
    }
    setPayrollDownloading(true);
    try {
      const resp = await axios.get('/api/hr/payroll-export', {
        params: { from: payrollFrom, to: payrollTo },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `payroll-export-${payrollFrom}-to-${payrollTo}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Payroll CSV downloaded');
      setPayrollDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate payroll export');
    } finally {
      setPayrollDownloading(false);
    }
  };

  // Listen for theme changes
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const isDark = document.documentElement.classList.contains('dark');
          setIsDarkMode(isDark);
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get('/api/teams/members');
      // API returns array directly, not wrapped in members property
      setTeamMembers(response.data || []);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const fetchLeaveTypes = async () => {
    try {
      const response = await axios.get('/api/leaves/types');
      setLeaveTypes(response.data || []);
    } catch (error) {
      console.error('Error fetching leave types:', error);
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      
      // Fetch all analytics data in parallel, passing the department filter
      // if one is selected. All endpoints accept an optional departmentId
      // query param server-side.
      const params = departmentId ? { departmentId } : {};
      const [
        overviewRes,
        userBalancesRes,
        popularDaysRes,
        topUsersRes,
        loginActivityRes,
        approversRes,
        trendsRes,
        utilizationRes,
        approvalTimesRes
      ] = await Promise.all([
        axios.get('/api/analytics/overview', { params }),
        axios.get('/api/analytics/user-balances', { params }),
        axios.get('/api/analytics/popular-days', { params }),
        axios.get('/api/analytics/top-users', { params }),
        axios.get('/api/analytics/login-activity', { params }),
        axios.get('/api/analytics/approvers', { params }),
        axios.get('/api/analytics/trends', { params }),
        axios.get('/api/analytics/utilization', { params }),
        axios.get('/api/analytics/approval-times', { params })
      ]);

      setOverview(overviewRes.data.data);
      setUserBalances(userBalancesRes.data.data);
      setPopularDays(popularDaysRes.data.data);
      setTopUsers(topUsersRes.data.data);
      setLoginActivity(loginActivityRes.data.data);
      setApprovers(approversRes.data.data);
      setTrends(trendsRes.data.data);
      setUtilization(utilizationRes.data.data);
      setApprovalTimes(approvalTimesRes.data.data);
      
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const [sickWarnings, setSickWarnings] = useState([]);

  const fetchSickLeaveData = async () => {
    if (!sickLeaveTypeId) {
      toast.error('Please select a leave type for sick leave analysis');
      return;
    }

    try {
      const params = { sickLeaveTypeId };
      if (departmentId) params.departmentId = departmentId;
      const response = await axios.get('/api/analytics/sick-leave', { params });
      setSickLeave(response.data.data);
      setSickWarnings(response.data.warnings || []);
    } catch (error) {
      console.error('Error fetching sick leave data:', error);
      toast.error('Failed to load sick leave data');
    }
  };

  const fetchUserSpecificData = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }
    
    try {
      // Use the same endpoint as the working TeamMember page
      const response = await axios.get(`/api/leaves/balances/${selectedUserId}`);
      console.log('User balance API response:', response.data);
      
      // The endpoint now returns { "AL": { allocated: 20, used: 7, remaining: 13, adjustments: 0 } } format
      // Convert this to the format expected by the UI
      const balanceData = response.data;
      const balanceArray = Object.entries(balanceData).map(([leaveTypeName, balanceInfo]) => ({
        leaveTypeName,
        allocated: balanceInfo.allocated || 0,
        used: balanceInfo.used || 0,
        remaining: balanceInfo.remaining || 0,
        adjustments: balanceInfo.adjustments || 0
      }));
      
      console.log('Converted balances:', balanceArray);
      setUserBalances(balanceArray);
    } catch (error) {
      console.error('Error fetching user data:', error);
      toast.error('Failed to load user data');
    }
  };

  const exportChart = (chartId, filename) => {
    const canvas = document.getElementById(chartId);
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `${filename}.png`;
      link.href = url;
      link.click();
    }
  };

  const exportCSV = (data, filename) => {
    const csvContent = convertToCSV(data);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';

    const headers = Object.keys(data[0]);
    const csvRows = [headers.join(',')];

    data.forEach(row => {
      const values = headers.map(header => {
        const value = row[header];
        return typeof value === 'string' ? `"${value}"` : value;
      });
      csvRows.push(values.join(','));
    });

    return csvRows.join('\n');
  };

  // Small reusable button rendered next to each data-table header.
  // Disabled when there's nothing to export (prevents empty CSVs).
  const TableCsvButton = ({ rows, filename }) => (
    <button
      type="button"
      onClick={() => exportCSV(rows || [], filename)}
      disabled={!rows || rows.length === 0}
      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white/60 dark:bg-gray-800/60 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
      title={rows && rows.length > 0 ? 'Download this table as CSV' : 'No rows to export'}
    >
      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
      </svg>
      CSV
    </button>
  );

  // Dynamic chart configurations that respond to theme changes
  const getChartOptions = () => {
    const isDark = document.documentElement.classList.contains('dark');
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            color: isDark ? '#e5e7eb' : '#374151',
            font: {
              size: 12
            }
          }
        },
        title: {
          display: false
        },
        tooltip: {
          backgroundColor: isDark ? '#1f2937' : '#ffffff',
          titleColor: isDark ? '#f9fafb' : '#111827',
          bodyColor: isDark ? '#e5e7eb' : '#374151',
          borderColor: isDark ? '#374151' : '#e5e7eb',
          borderWidth: 1
        }
      },
      scales: {
        x: {
          ticks: {
            color: isDark ? '#9ca3af' : '#6b7280',
            font: {
              size: 11
            }
          },
          grid: {
            color: isDark ? '#374151' : '#e5e7eb',
            borderColor: isDark ? '#4b5563' : '#d1d5db'
          }
        },
        y: {
          ticks: {
            color: isDark ? '#9ca3af' : '#6b7280',
            font: {
              size: 11
            }
          },
          grid: {
            color: isDark ? '#374151' : '#e5e7eb',
            borderColor: isDark ? '#4b5563' : '#d1d5db'
          }
        }
      }
    };
  };

  const getPieChartOptions = () => {
    const isDark = document.documentElement.classList.contains('dark');
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
          labels: {
            color: isDark ? '#e5e7eb' : '#374151',
            font: {
              size: 12
            },
            padding: 20,
            usePointStyle: true
          }
        },
        tooltip: {
          backgroundColor: isDark ? '#1f2937' : '#ffffff',
          titleColor: isDark ? '#f9fafb' : '#111827',
          bodyColor: isDark ? '#e5e7eb' : '#374151',
          borderColor: isDark ? '#374151' : '#e5e7eb',
          borderWidth: 1
        }
      }
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900 dark:to-indigo-900">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent animate-fade-in">
              Analytics Dashboard
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2 animate-fade-in">
              Comprehensive insights into your team's leave patterns and utilisation
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Department filter — only appears when departments are configured */}
            {departments.length > 0 && (
              <select
                value={departmentId}
                onChange={(e) => setDepartmentId(e.target.value)}
                className="rounded-md border-gray-300 bg-white/60 dark:bg-gray-800/60 dark:border-gray-600 dark:text-white text-sm px-3 py-2 backdrop-blur-sm"
                aria-label="Filter analytics by department"
              >
                <option value="">All departments</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            )}

          {/* Export All Button */}
          <GlassButton
            onClick={() => {
              // Export overview data as CSV
              const overviewData = [
                {
                  metric: 'Total Team Members',
                  value: overview.totalTeamMembers || 0
                },
                {
                  metric: 'Total Days Taken',
                  value: overview.totalDaysTaken || 0
                },
                {
                  metric: 'Average Days Per Person',
                  value: overview.avgDaysPerPerson || 0
                },
                {
                  metric: 'Utilization Rate (%)',
                  value: overview.utilizationRate || 0
                }
              ];
              exportCSV(overviewData, 'analytics-overview');
            }}
            className="flex items-center gap-2"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export Data
          </GlassButton>

          <GlassButton
            onClick={() => setPayrollDialogOpen(true)}
            className="flex items-center gap-2"
            title="Download a CSV for your payroll provider: one row per team member, with holiday / sick / unpaid day counts for the selected period."
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8v1m0 8v1M5 12a7 7 0 1114 0 7 7 0 01-14 0z" />
            </svg>
            Payroll Export
          </GlassButton>
          </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-8">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', name: 'Overview', icon: '📊' },
            { id: 'users', name: 'User Analysis', icon: '👥' },
            { id: 'trends', name: 'Trends', icon: '📈' },
            { id: 'utilisation', name: 'Utilisation', icon: '⚡' },
            { id: 'approvals', name: 'Approvals', icon: '✅' },
            { id: 'activity', name: 'Activity', icon: '🕒' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <span>{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-8">
          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <GlassCard className="p-6 hover:shadow-glow hover:scale-[1.02] animate-fade-in">
              <div className="flex items-center">
                <div className="p-3 rounded-lg bg-gradient-to-br from-blue-400 to-cyan-500 text-white shadow-lg">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-300 flex items-center gap-1">
                    Team Members
                    <span title="Active team members counted in analytics. Excludes deactivated users. Scoped to the currently-selected department when the filter is set." className="cursor-help text-gray-400 text-xs">ⓘ</span>
                  </p>
                  <p className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                    {overview.totalTeamMembers || 0}
                  </p>
                </div>
              </div>
            </GlassCard>

            <GlassCard className="p-6 hover:shadow-glow hover:scale-[1.02] animate-fade-in">
              <div className="flex items-center">
                <div className="p-3 rounded-lg bg-gradient-to-br from-green-400 to-emerald-500 text-white shadow-lg">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-300 flex items-center gap-1">
                    Days Taken
                    <span title="Total approved leave days taken in the current business year. Half-days count as 0.5. Pending and rejected requests are excluded." className="cursor-help text-gray-400 text-xs">ⓘ</span>
                  </p>
                  <p className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                    {overview.totalDaysTaken || 0}
                  </p>
                </div>
              </div>
            </GlassCard>

            <GlassCard className="p-6 hover:shadow-glow hover:scale-[1.02] animate-fade-in">
              <div className="flex items-center">
                <div className="p-3 rounded-lg bg-gradient-to-br from-purple-400 to-indigo-500 text-white shadow-lg">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-300 flex items-center gap-1">
                    Avg Per Person
                    <span title="Total days taken divided by the number of active team members. Rounded to 1 decimal." className="cursor-help text-gray-400 text-xs">ⓘ</span>
                  </p>
                  <p className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                    {overview.avgDaysPerPerson || 0}
                  </p>
                </div>
              </div>
            </GlassCard>

            <GlassCard className="p-6 hover:shadow-glow hover:scale-[1.02] animate-fade-in">
              <div className="flex items-center">
                <div className="p-3 rounded-lg bg-gradient-to-br from-orange-400 to-red-500 text-white shadow-lg">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-300 flex items-center gap-1">
                    Utilization
                    <span title="Days used ÷ total days allocated × 100. Higher = team is using their allowance; lower = people may need nudges to book time off." className="cursor-help text-gray-400 text-xs">ⓘ</span>
                  </p>
                  <p className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                    {overview.utilizationRate || 0}%
                  </p>
                </div>
              </div>
            </GlassCard>
          </div>

          {/* Documents expiring tile — visible whenever there's anything to
              show. Clicking opens a modal with the full list. */}
          {expiringDocs.length > 0 && (
            <GlassCard
              className="p-4 mb-6 cursor-pointer hover:shadow-glow transition-shadow"
              onClick={() => setShowExpiringModal(true)}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/90 text-white shadow">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                    Documents needing attention
                  </div>
                  <div className="text-lg font-bold text-gray-900 dark:text-white">
                    {expiringDocs.length} expiring or expired in the next 30 days
                  </div>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Click to view</div>
              </div>
            </GlassCard>
          )}

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Popular Days Chart */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Popular Days of Week</h3>
                <button
                  onClick={() => exportChart('popular-days-chart', 'popular-days')}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </button>
              </div>
              <div className="h-64">
                {popularDays.popularDaysOfWeek && (
                  <Bar
                    id="popular-days-chart"
                    data={{
                      labels: popularDays.popularDaysOfWeek.map(d => d.day),
                      datasets: [{
                        label: 'Leave Days Taken',
                        data: popularDays.popularDaysOfWeek.map(d => d.count),
                        backgroundColor: 'rgba(59, 130, 246, 0.5)',
                        borderColor: 'rgba(59, 130, 246, 1)',
                        borderWidth: 1
                      }]
                    }}
                    options={getChartOptions()}
                  />
                )}
              </div>
            </div>

            {/* Leave Type Distribution */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Leave Type Distribution</h3>
                <button
                  onClick={() => exportChart('leave-types-chart', 'leave-types')}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </button>
              </div>
              <div className="h-64">
                {trends.leaveTypeDistribution && (
                  <Doughnut
                    id="leave-types-chart"
                    data={{
                      labels: trends.leaveTypeDistribution.map(t => t.leaveType),
                      datasets: [{
                        data: trends.leaveTypeDistribution.map(t => t.days),
                        backgroundColor: trends.leaveTypeDistribution.map(t => t.color || '#6366f1')
                      }]
                    }}
                    options={getPieChartOptions()}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Analysis Tab */}
      {activeTab === 'users' && (
        <div className="space-y-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex flex-col md:flex-row gap-4 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select User for Analysis
                </label>
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">All Users</option>
                  {teamMembers.map(member => (
                    <option key={member.id} value={member.id}>
                      {member.firstName} {member.lastName}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={fetchUserSpecificData}
                disabled={!selectedUserId}
                className="bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg"
              >
                Analyse User
              </button>
            </div>
          </div>

          {/* User-Specific Analysis Results */}
          {selectedUserId && userBalances && userBalances.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Analysis for {teamMembers.find(m => m.id === selectedUserId)?.firstName} {teamMembers.find(m => m.id === selectedUserId)?.lastName}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {userBalances.map((balance, index) => (
                  <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{balance.leaveTypeName}</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Allocated:</span>
                        <span className="text-gray-900 dark:text-white">{balance.allocated || 0} days</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Used:</span>
                        <span className="text-gray-900 dark:text-white">{balance.used || 0} days</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Remaining:</span>
                        <span className={`font-semibold ${
                          (balance.remaining || 0) > 5 ? 'text-green-600 dark:text-green-400' :
                          (balance.remaining || 0) > 2 ? 'text-yellow-600 dark:text-yellow-400' :
                          'text-red-600 dark:text-red-400'
                        }`}>{balance.remaining || 0} days</span>
                      </div>
                      {balance.adjustments !== 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Adjustments:</span>
                          <span className={`${balance.adjustments > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {balance.adjustments > 0 ? '+' : ''}{balance.adjustments} days
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Top Leave Takers</h3>
              <TableCsvButton
                rows={(topUsers || []).map(u => ({ name: u.user?.name, email: u.user?.email, totalDays: u.totalDays }))}
                filename="top-leave-takers"
              />
            </div>
            <div className="h-64">
              {topUsers.length > 0 && (
                <Bar
                  data={{
                    labels: topUsers.slice(0, 10).map(u => u.user.name.split(' ')[0]),
                    datasets: [{
                      label: 'Days Taken',
                      data: topUsers.slice(0, 10).map(u => u.totalDays),
                      backgroundColor: 'rgba(16, 185, 129, 0.5)',
                      borderColor: 'rgba(16, 185, 129, 1)',
                      borderWidth: 1
                    }]
                  }}
                  options={getChartOptions()}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Trends Tab */}
      {activeTab === 'trends' && (
        <div className="space-y-8">
          {/* Monthly Trends */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Monthly Leave Trends</h3>
              <button
                onClick={() => exportChart('monthly-trends-chart', 'monthly-trends')}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </button>
            </div>
            <div className="h-80">
              {trends.monthlyTrends && trends.monthlyTrends.length > 0 ? (
                <Line
                  id="monthly-trends-chart"
                  data={{
                    labels: trends.monthlyTrends.map(t => t.monthName),
                    datasets: [
                      {
                        label: 'Days Taken',
                        data: trends.monthlyTrends.map(t => t.days),
                        borderColor: 'rgba(59, 130, 246, 1)',
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        fill: true,
                        tension: 0.4
                      },
                      {
                        label: 'Requests',
                        data: trends.monthlyTrends.map(t => t.requests),
                        borderColor: 'rgba(16, 185, 129, 1)',
                        backgroundColor: 'rgba(16, 185, 129, 0.1)',
                        fill: true,
                        tension: 0.4,
                        yAxisID: 'y1'
                      }
                    ]
                  }}
                  options={{
                    ...getChartOptions(),
                    scales: {
                      ...getChartOptions().scales,
                      y1: {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        ticks: {
                          color: document.documentElement.classList.contains('dark') ? '#9ca3af' : '#6b7280',
                          font: {
                            size: 11
                          }
                        },
                        grid: {
                          drawOnChartArea: false,
                          color: document.documentElement.classList.contains('dark') ? '#374151' : '#e5e7eb'
                        }
                      }
                    }
                  }}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                  No trend data available
                </div>
              )}
            </div>
          </div>

          {/* Popular Dates */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Most Popular Leave Dates</h3>
              <button
                onClick={() => exportCSV(popularDays.popularDates || [], 'popular-dates')}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {popularDays.popularDates?.slice(0, 9).map((date, index) => (
                <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {new Date(date.date).toLocaleDateString()}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {date.dayName}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-primary-600 dark:text-primary-400">
                        {date.count}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">people</p>
                    </div>
                  </div>
                </div>
              )) || (
                <div className="col-span-full text-center text-gray-500 dark:text-gray-400">
                  No popular dates data available
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Utilisation Tab */}
      {activeTab === 'utilisation' && (
        <div className="space-y-8">
          {/* Overall Utilisation */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Team Utilisation Overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary-600 dark:text-primary-400">
                  {utilization.overallUtilization || 0}%
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center justify-center gap-1">
                  Overall Utilisation
                  <span title="Total days used ÷ total days allocated, across all active members." className="cursor-help text-gray-400 text-xs">ⓘ</span>
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {utilization.totalUsed || 0}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Days Used</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  {utilization.totalAllocated || 0}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Days Allocated</div>
              </div>
            </div>
          </div>

          {/* User Utilisation Table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Individual Utilisation</h3>
              <TableCsvButton
                rows={(utilization.userUtilization || []).map(u => ({
                  name: u.user?.name,
                  allocated: u.allocated,
                  used: u.used,
                  remaining: u.remaining,
                  utilizationRate: u.utilizationRate,
                  healthStatus: u.healthStatus
                }))}
                filename="individual-utilisation"
              />
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Allocated
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Used
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Remaining
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Utilisation
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Health
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {utilization.userUtilization?.map((user, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {user.user.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {user.allocated}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {user.used}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {user.remaining}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {user.utilizationRate}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.healthStatus === 'healthy' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                          user.healthStatus === 'high-usage' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                          user.healthStatus === 'low-usage' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                          'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}>
                          {user.healthStatus?.replace('-', ' ') || 'Unknown'}
                        </span>
                      </td>
                    </tr>
                  )) || (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                        No utilisation data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Approvals Tab */}
      {activeTab === 'approvals' && (
        <div className="space-y-8">
          {/* Approval Times */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Approval Response Times</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  {approvalTimes.avgResponseTimeHours || 0}h
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Average Response Time</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {approvalTimes.medianResponseTimeHours || 0}h
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Median Response Time</div>
              </div>
            </div>
            
            {/* Response Time Distribution */}
            <div className="h-64">
              {approvalTimes.timeRangeDistribution && Object.keys(approvalTimes.timeRangeDistribution).length > 0 ? (
                <Bar
                  data={{
                    labels: Object.keys(approvalTimes.timeRangeDistribution),
                    datasets: [{
                      label: 'Number of Requests',
                      data: Object.values(approvalTimes.timeRangeDistribution),
                      backgroundColor: 'rgba(139, 92, 246, 0.5)',
                      borderColor: 'rgba(139, 92, 246, 1)',
                      borderWidth: 1
                    }]
                  }}
                  options={getChartOptions()}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                  No approval time data available
                </div>
              )}
            </div>
          </div>

          {/* Top Approvers */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Top Approvers</h3>
              <TableCsvButton
                rows={(approvers || []).map(a => ({
                  name: a.user?.name,
                  email: a.user?.email,
                  totalApproved: a.totalApproved,
                  totalDays: a.totalDays
                }))}
                filename="top-approvers"
              />
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Approver
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Total Approvals
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Days Approved
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {approvers.length > 0 ? approvers.map((approver, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {approver.approver.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {approver.totalApprovals}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {approver.totalDaysApproved}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="3" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                        No approver data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Activity Tab */}
      {activeTab === 'activity' && (
        <div className="space-y-8">
          {/* Sick Leave Analysis */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex flex-col md:flex-row gap-4 items-end mb-6">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Leave Type for Sick Leave Analysis
                </label>
                <select
                  value={sickLeaveTypeId}
                  onChange={(e) => setSickLeaveTypeId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select leave type...</option>
                  {leaveTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Pick the leave type your team uses for sickness tracking.
                </p>
              </div>
              <button
                onClick={fetchSickLeaveData}
                disabled={!sickLeaveTypeId}
                className="bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg"
              >
                Analyse Sick Leave
              </button>
            </div>

            {sickWarnings.length > 0 && (
              <div className="mb-4 p-3 rounded-md border border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-200 text-sm">
                {sickWarnings.map((w, i) => <div key={i}>{w}</div>)}
              </div>
            )}

            {sickLeave.userSickData && sickLeave.userSickData.length > 0 && (
              <>
                <div className="flex justify-end mb-2">
                  <TableCsvButton
                    rows={sickLeave.userSickData.map(u => ({
                      name: u.user?.name,
                      totalSickDays: u.totalSickDays,
                      sickLeaveCount: u.sickLeaveCount
                    }))}
                    filename="sick-leave-analysis"
                  />
                </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Sick Days
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Sick Leaves
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {sickLeave.userSickData.map((user, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {user.user.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {user.totalSickDays}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {user.sickLeaveCount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </div>

          {/* Login Activity */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Login Activity</h3>
              <TableCsvButton
                rows={(loginActivity || []).map(u => ({
                  name: u.user?.name || `${u.firstName || ''} ${u.lastName || ''}`.trim(),
                  email: u.user?.email || u.email,
                  lastLogin: u.lastLogin,
                  daysSinceLogin: u.daysSinceLogin,
                  memberSince: u.memberSince || u.createdAt
                }))}
                filename="login-activity"
              />
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Days Since Login
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Member Since
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {loginActivity.length > 0 ? loginActivity.map((user, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {user.user.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {user.daysSinceLogin !== null ? user.daysSinceLogin : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {new Date(user.memberSince).toLocaleDateString()}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="4" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                        No login activity data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Expiring-documents modal */}
      {showExpiringModal && (
        <div className="fixed inset-0 z-50 bg-gray-900/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Documents needing attention ({expiringDocs.length})
              </h3>
              <button onClick={() => setShowExpiringModal(false)} className="text-gray-500 hover:text-gray-900 dark:text-gray-400">✕</button>
            </div>
            <div className="p-6">
              {expiringDocs.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Nothing expiring in the next 30 days.</p>
              ) : (
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {expiringDocs.map(d => {
                    const exp = new Date(d.expiresAt);
                    const today = new Date(); today.setHours(0,0,0,0);
                    const daysUntil = Math.floor((exp - today) / (1000*60*60*24));
                    const expired = daysUntil < 0;
                    return (
                      <li key={d.id} className="py-3 flex items-start justify-between">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{d.title}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {d.firstName} {d.lastName} · {d.documentType || 'Document'} · expires {exp.toLocaleDateString()}
                          </div>
                        </div>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${expired ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'}`}>
                          {expired ? 'Expired' : `${daysUntil}d`}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payroll export dialog */}
      {payrollDialogOpen && (
        <div className="fixed inset-0 z-50 bg-gray-900/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Payroll export</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Download a CSV for your payroll provider. One row per active
                team member with their absence totals for the selected period.
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">From</label>
                  <input
                    type="date"
                    value={payrollFrom}
                    onChange={e => setPayrollFrom(e.target.value)}
                    className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">To</label>
                  <input
                    type="date"
                    value={payrollTo}
                    onChange={e => setPayrollTo(e.target.value)}
                    className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm sm:text-sm"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Leave types with names containing &quot;sick&quot;, &quot;unpaid&quot;, or
                &quot;holiday&quot;/&quot;annual&quot;/&quot;vacation&quot; are auto-classified. Anything else lands in the &quot;other&quot; column and is named in the row&apos;s notes.
              </p>
            </div>
            <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700 flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => setPayrollDialogOpen(false)}
                disabled={payrollDownloading}
                className="py-2 px-4 border border-gray-300 dark:border-gray-500 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50"
              >
                Cancel
              </button>
              <GlassButton onClick={downloadPayrollCsv} disabled={payrollDownloading}>
                {payrollDownloading ? 'Generating…' : 'Download CSV'}
              </GlassButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
